import {isLoggedIn} from '@github-ui/client-env'
import {dismissUserNoticePath} from '@github-ui/paths'
import {useMutation} from '@github-ui/react-query'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import type {PullRequestNotices} from '../page-data/payloads/notices'
import {useIsNoticeDismissed, useUserNoticesActions} from '../stores/layout-selectors'

/**
 * Hook to dismiss a user notice.
 *
 * @param name - The notice name to dismiss
 * @returns Object containing dismissNotice function and mutation state
 */
export function useDismissNotice(name: PullRequestNotices) {
  const noticeIsDismissed = useIsNoticeDismissed(name)

  const {dismissNotice: dismissStoredNotice} = useUserNoticesActions()
  const {mutate: dismissNoticeMutation} = useMutation({
    mutationFn: async (noticeName: PullRequestNotices) => {
      await verifiedFetch(dismissUserNoticePath({noticeName}), {method: 'POST'})
    },
    onSuccess: (_data, dismissedNotice) => {
      dismissStoredNotice(dismissedNotice)
    },
  })

  const dismissNotice = useCallback(() => {
    // Don't dismiss the notice if the user is not logged in or the notice is already dismissed
    if (noticeIsDismissed) return
    if (!isLoggedIn()) return

    dismissNoticeMutation(name)
  }, [dismissNoticeMutation, name, noticeIsDismissed])

  return {dismissNotice}
}
